'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { CirclePlay } from 'lucide-react'
import dynamic from 'next/dynamic'
import { getVideoById } from '@homie/video-player'

// Lazy-load VideoOverlay (and through it, MuxPlayer + hls.js — a ~1.28 MB chunk)
// so the player JS doesn't ship until the user actually clicks "Watch demo".
// ssr:false because the overlay is interactive-only; no SEO content needed.
const VideoOverlay = dynamic(
  () => import('@homie/video-player').then((m) => m.VideoOverlay),
  { ssr: false }
)

type VideoButtonProps = {
  videoId: string
  label?: string
  className?: string
  showDuration?: boolean
}

/**
 * A button that displays a video ID's title and opens the video in an overlay.
 * Pass videoId like 'calendar', 'routines', 'tasks', 'meals', 'agenda', etc.
 */
export function VideoButton({ videoId, label, className = '', showDuration = true }: VideoButtonProps) {
  const t = useTranslations('features')
  const [isOpen, setIsOpen] = useState(false)
  const video = getVideoById(videoId)

  if (!video) {
    console.warn(`VideoButton: Video with id "${videoId}" not found`)
    return null
  }

  // Default label format: "Demo"
  const displayLabel = label || t('videoButtonLabel')

  return (
    <>
      {/* flex-wrap so the duration drops to a second line instead of spilling
          out of the button. Inside the features carousel this row can end up in
          a ~107px column at 360px, which is narrower than "Demo (0:49)"; before
          this it overflowed the button and gave the whole page a horizontal
          scrollbar. No effect wherever there is room. */}
      <button
        onClick={() => setIsOpen(true)}
        className={`flex flex-wrap items-center gap-1.5 text-black cursor-pointer ${className}`}
      >
        <CirclePlay size={24} strokeWidth={1.5} className="shrink-0" />
        <span className="text-m">
          {displayLabel}
        </span>
        {showDuration && <span className="text-m text-gray-500">({video.duration})</span>}
      </button>

      {/* Only mount the overlay (and its heavy player chunk) once opened */}
      {isOpen && (
        <VideoOverlay
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          playbackId={video.playbackId}
          title={video.title}
          aspectRatio="portrait"
        />
      )}
    </>
  )
}
